import { Injectable } from "@angular/core";
import { AppConfig } from "../configs/app";
import { CommonService } from "../services/common.service";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  constructor(
    public appConfig: AppConfig,
    public commonService: CommonService
  ) {}

  login(userData = {}) {
    localStorage.setItem(
      this.appConfig.auth.loginSessionName,
      JSON.stringify(userData)
    );
  }

  isLoggedIn() {
    if (localStorage.getItem(this.appConfig.auth.loginSessionName) === null) {
      return false;
    }
    return true;
  }

  getUserData() {
    return JSON.parse(
      localStorage.getItem(this.appConfig.auth.loginSessionName)
    );
  }

  logout() {
    /* REMOVE TEMP CACHES */
    this.commonService.removeBatchData([
      this.appConfig.auth.currentURLSessionName,
      this.appConfig.plan.acdCodeSessionName,
      this.appConfig.reservation.passengerSessionName,
      this.appConfig.reservation.reservationDataSessionName,
      this.appConfig.reservation.insuranceDataSessionName,
      this.appConfig.reservation.insuranceTokenSessionName,
      this.appConfig.reservation.reservationCompleteDataSessionName,
      this.appConfig.reservation.insurancePassengerData1SessionName,
      this.appConfig.reservation.paymentCalculationDataSessionName,
      this.appConfig.reservation.couponDataName,
      this.appConfig.reservation.errorCreditCardSessionName,
      this.appConfig.reservation.changeReservationDataSessionName,
      this.appConfig.plan.referrerSessionName,
      "searchReservationParams",
      "searchReservationDataDetail",
      "searchReservationDataEmail",
      "onSubmitReservationChanging",
    ]);

    /* REMOVE USER DATA */
    localStorage.removeItem(this.appConfig.auth.loginSessionName);
  }

  /* EMAIL */
  async loginWithEmail(formData: any) {
    /* SET LAST URL */
    this.commonService.setCurrentURL(window.location.href);

    let params = {
      url: this.appConfig.busAPIBaseURL + "auth/login",
      data: `username=${formData.email}&password=${formData.password}`,
      options: {
        headers: this.commonService.getHTTPBusHeader(),
      },
    };

    let returnData = await this.commonService.sendHTTPPost(params);
    if (returnData.status == "success") {
      this.login(returnData.data);
      return { status: "success" };
    } else {
      return { status: "fail", message: returnData.message };
    }
  }

  /*
   * [F-8] OAuth state CSRF対策ユーティリティ
   *
   * 【重要: フロントエンド・バックエンド同時デプロイ必須】
   * この修正は AuthController.php 側の修正とセットです。
   * フロントエンドだけデプロイするとバックエンドがstateを返さず検証が必ず失敗します。
   * バックエンドだけデプロイするとフロントエンドが固定stateを送り検証が必ず失敗します。
   * 必ず両方同時にデプロイしてください。
   *
   * 対象バックエンドファイル: app/Http/Controllers/API/AuthController.php
   *   - loginWithFacebook(): stateをクエリパラメータとしてリダイレクトURLに付与
   *   - loginWithLineWithAccessToken(): stateをJSONレスポンスに含める
   */

  /**
   * OAuth用のランダムstateを生成してsessionStorageに保存する。
   * RFC 6749 Section 10.12 に基づくCSRF対策。
   * @param provider "facebook" | "line" など、プロバイダ識別子
   * @returns 生成されたstate文字列
   */
  generateOAuthState(provider: string): string {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    const state = Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
    sessionStorage.setItem(`oauth_state_${provider}`, state);
    return state;
  }

  /**
   * OAuth コールバック後にstateを検証する。
   * sessionStorageに保存したstateと比較し、一致しなければCSRF攻撃の可能性。
   * @param provider プロバイダ識別子
   * @param returnedState コールバックで返されたstate
   * @returns true: 検証OK, false: 不一致（CSRF疑い）
   */
  verifyOAuthState(provider: string, returnedState: string): boolean {
    const savedState = sessionStorage.getItem(`oauth_state_${provider}`);
    sessionStorage.removeItem(`oauth_state_${provider}`); // 使い捨て（リプレイ攻撃防止）
    if (!savedState || savedState !== returnedState) {
      console.error(`[F-8] OAuth state mismatch for ${provider}. Expected: ${savedState}, Got: ${returnedState}. Possible CSRF attack.`);
      return false;
    }
    return true;
  }

  /* FACEBOOK */
  redirectToFacebookLogin() {
    // [F-8] 固定state "123123" → ランダム生成に変更（CSRF対策）
    const state = this.generateOAuthState('facebook');
    window.location.href = `${this.appConfig.social.facebook.postURL}?client_id=${this.appConfig.social.facebook.clientID}&redirect_uri=${this.appConfig.social.facebook.redirectUri}&state=${state}&response_type=${this.appConfig.social.facebook.response_type}`;
  }

  async loginWithFacebookToken(accessToken: string) {
    let params = {
      url: this.appConfig.busAPIBaseURL + "auth/loginWithFacebookToken",
      data: `access_token=${accessToken}`,
      options: {
        headers: this.commonService.getHTTPBusHeader(),
      },
    };

    let returnData = await this.commonService.sendHTTPPost(params);
    if (returnData.status == "success") {
      this.login(returnData.data);
      return { status: "success", data: returnData.data };
    } else {
      return { status: "fail", message: returnData.message };
    }
  }

  /* LINE */
  redirectToLineLogin() {
    // [F-8] 固定state "12345" → ランダム生成に変更（CSRF対策）
    const state = this.generateOAuthState('line');
    window.location.href = `${this.appConfig.social.line.authorizeURL}?response_type=${this.appConfig.social.line.responseType}&client_id=${this.appConfig.social.line.clientID}&redirect_uri=${this.appConfig.busAPIBaseURL}auth/loginWithLine&state=${state}&scope=${this.appConfig.social.line.scope}`;
  }

  /**
   * [F-8] LINE OAuth state検証付きログイン
   *
   * 【重要: フロントエンド・バックエンド同時デプロイ必須】
   * バックエンド AuthController.php loginWithLineWithAccessToken() が
   * レスポンスJSON に oauth_state を含めて返すようになっている必要があります。
   * バックエンドが旧バージョンのままだと oauth_state が undefined になり、
   * state検証が必ず失敗してLINEログインできなくなります。
   *
   * 注意: 現状のLINEログインフローでは loginWithLineWithAccessToken がGETルートで
   * LINEからの直接コールバックとしてJSON応答を返すため、このメソッドに到達しない
   * 可能性があります（LINE_REDIRECT_URIがngrok URLに設定されており開発途中の模様）。
   * LINEログインフロー全体の修正が別途必要です。
   */
  async loginWithLineAccessToken(token: string) {
    let params = {
      url: this.appConfig.busAPIBaseURL + "auth/loginWithLineWithAccessToken",
      data: `access_token=${token}`,
      options: {
        headers: this.commonService.getHTTPBusHeader(),
      },
    };
    let returnData = await this.commonService.sendHTTPPost(params);
    if (returnData.status == "success") {
      // [F-8] LINE OAuth state検証
      // バックエンドがレスポンスに含めた oauth_state をsessionStorageの値と比較
      if (returnData.oauth_state !== undefined) {
        if (!this.verifyOAuthState('line', returnData.oauth_state)) {
          return { status: "fail", message: "セキュリティ検証に失敗しました。再度ログインしてください。" };
        }
      }
      this.login(returnData.data);
      return { status: "success", data: returnData.data };
    } else {
      return { status: "fail", message: returnData.message };
    }
  }

  /* GOOGLE */
  redirectToGoogleLogin() {
    window.location.href = `${this.appConfig.social.google.loginURL}?response_type=${this.appConfig.social.google.responseType}&client_id=${this.appConfig.social.google.clientID}&redirect_uri=${this.appConfig.busAPIBaseURL}auth/loginWithGoogle&scope=${this.appConfig.social.google.scope}`;
  }

  async loginWithGoogleAccessToken(token: string) {
    let params = {
      url: this.appConfig.busAPIBaseURL + "auth/loginWithGoogleToken",
      data: `access_token=${token}`,
      options: {
        headers: this.commonService.getHTTPBusHeader(),
      },
    };
    let returnData = await this.commonService.sendHTTPPost(params);
    if (returnData.status == "success") {
      this.login(returnData.data);
      return { status: "success", data: returnData.data };
    } else {
      return { status: "fail", message: returnData.message };
    }
  }

  /* YAHOO JP */
  redirectToYahooJPLogin() {
    window.location.href = `${this.appConfig.social.yahoo.loginURL}?response_type=${this.appConfig.social.yahoo.responseType}&client_id=${this.appConfig.social.yahoo.clientID}&redirect_uri=${this.appConfig.busAPIBaseURL}auth/loginWithYahoo&state=&scope=${this.appConfig.social.yahoo.scope}`;
  }

  async loginWithYahooAccessToken(token: string) {
    let params = {
      url: this.appConfig.busAPIBaseURL + "auth/loginWithYahooToken",
      data: `access_token=${token}`,
      options: {
        headers: this.commonService.getHTTPBusHeader(),
      },
    };
    let returnData = await this.commonService.sendHTTPPost(params);
    if (returnData.status == "success") {
      this.login(returnData.data);
      return { status: "success", data: returnData.data };
    } else {
      return { status: "fail", message: returnData.message };
    }
  }

  /* INSTAGRAM */
  redirectToInstagramLogin() {
    const state = this.generateOAuthState('instagram');
    window.location.href = `${this.appConfig.social.instagram.authorizeURL}?response_type=${this.appConfig.social.instagram.responseType}&client_id=${this.appConfig.social.instagram.clientID}&redirect_uri=${this.appConfig.busAPIBaseURL}auth/loginWithLine&state=${state}&scope=${this.appConfig.social.instagram.scope}`;
  }

  async loginWithInstagramAccessToken(token: string) {
    /* TO DO */
  }

  /* TWITTER */
  redirectToTwitterLogin() {
    window.location.href = `${this.appConfig.social.twitter.loginURL}`;
  }

  async loginWithTwitterAccessToken(data: any) {
    /* TO DO */
    let params = {
      url: this.appConfig.busAPIBaseURL + "auth/loginWithTwitterToken",
      data: `oauthToken=${data.oauthToken}&oauthVerifier=${data.oauthVerifier}`,
      options: {
        headers: this.commonService.getHTTPBusHeader(),
      },
    };
    let returnData = await this.commonService.sendHTTPPost(params);
    if (returnData.status == "success") {
      this.login(returnData.data);
      return { status: "success", data: returnData.data };
    } else {
      return { status: "fail", message: returnData.message };
    }
  }

  async forgotPassword(formData: any) {
    let params = {
      url: this.appConfig.busAPIBaseURL + "auth/forgotPassword",
      data: `email=${formData.email}&year=${formData.year}&month=${formData.month}&day=${formData.day}`,
      options: {
        headers: this.commonService.getHTTPBusHeader(),
      },
    };
    return await this.commonService.sendHTTPPost(params);
  }

  async sendTestMail(data: any) {
    let params = {
      url: this.appConfig.busAPIBaseURL + "auth/sendTestMail",
      options: {
        headers: this.commonService.getHTTPBusHeader(),
        params: {
          email1: data,
        },
      },
    };
    return await this.commonService.sendHTTPPost(params);
  }
}
